import React, { useState, useEffect } from 'react';
import { Modal, Card, Checkbox, Badge, Tooltip, Pagination, Row, Col, Button, Input, Switch, Tag } from 'antd';
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase, debounce } from 'lodash';
import AvatarS3 from '../../../shared/images/AvatarS3';
import moment from 'moment';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { useProjectContext } from '../../../../core/components/projects/useProjectContext';
import { urgencyColors } from '../configs/task.config';

const AddExistingTaskModal = ({ visible, onCancel, currentUser, currentProject, updateProject, refetchProject, onAddTasks, updateProjectLoading }) => {
  const { fetchMilestones, milestones, milestonesLoading } = useProjectContext();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [selectedTasksMap, setSelectedTasksMap] = useState({});  // Store full task objects by ID
  const [selectedMilestoneIds, setSelectedMilestoneIds] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [showMyTasks, setShowMyTasks] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [affectedUsers, setAffectedUsers] = useState([]);

  const { fetchTasksPaginated, tasksConnectionData, tasksLoading } = useTaskContext();

  useEffect(() => {
    if (visible) {
      handleFetchTasks();
      if (currentProject?.id) {
        fetchMilestones({
          variables: {
            where: { project: { id: currentProject.id } },
            orderBy: 'dueDate_ASC',
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, pagination.current, pagination.pageSize, showMyTasks, searchText]);

  const handleFetchTasks = () => {
    const skip = (pagination.current - 1) * pagination.pageSize;

    const currentProjectTaskIds = currentProject?.tasks?.map(task => task.id) || [];

    fetchTasksPaginated({
      variables: {
        first: pagination.pageSize,
        skip,
        orderBy: 'updatedAt_DESC',
        where: {
          AND: [
            { name_contains: searchText },
            ...(currentProjectTaskIds.length > 0 ? [{ id_not_in: currentProjectTaskIds }] : []),
            { project: null },
            ...(showMyTasks
              ? [
                  {
                    OR: [
                      { assigneeUser: { id: currentUser.id } },
                      { assigneeUsers_some: { id: currentUser.id } },
                      { createdBy: { id: currentUser.id } },
                      { followers_some: { id: currentUser.id } },
                      { collaborators_some: { id: currentUser.id } },
                    ],
                  },
                ]
              : []),
            {
              OR: [
                {
                  createdBy: {
                    roles_some: {
                      org: {
                        OR: [
                          { parentOrg_some: { id: currentUser.role.org.id } },
                          { id: currentUser.role.org.id },
                        ],
                      },
                    },
                  },
                },
              ],
            },
          ],
        },
      },
    });
  };

  const handleSearchChange = debounce((value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  }, 0);

  useEffect(() => {
    if (tasksConnectionData && tasksConnectionData.tasksConnection) {
      setPagination((prev) => ({
        ...prev,
        total: tasksConnectionData.full?.aggregate?.count || 0,
      }));
    }
  }, [tasksConnectionData]);

  const handleTaskSelect = (task) => {
    setSelectedTaskIds((prev) => {
      if (prev.includes(task.id)) {
        setSelectedTasksMap((prevMap) => {
          const newMap = { ...prevMap };
          delete newMap[task.id];
          return newMap;
        });
        return prev.filter((id) => id !== task.id);
      } else {
        setSelectedTasksMap((prevMap) => ({
          ...prevMap,
          [task.id]: task,
        }));
        return [...prev, task.id];
      }
    });
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination({
      ...pagination,
      current: page,
      pageSize: pageSize || pagination.pageSize,
    });
  };

  const handleCancel = () => {
    setCurrentStep(1);
    setSelectedTaskIds([]);
    setSelectedTasksMap({});
    setSelectedMilestoneIds([]);
    setPagination({ current: 1, pageSize: 10, total: 0 });
    setShowWarningModal(false);
    setAffectedUsers([]);
    onCancel();
  };

  const handleMilestoneSelect = (milestoneId) => {
    setSelectedMilestoneIds((prev) =>
      prev.includes(milestoneId) ? prev.filter((id) => id !== milestoneId) : [...prev, milestoneId]
    );
  };

  const handleNext = () => {
    if (selectedTaskIds.length === 0) {
      return;
    }
    setCurrentStep(2);
  };

  const tasks = tasksConnectionData?.tasksConnection?.edges?.map((edge) => edge.node) || [];

  const selectedTasks = Object.values(selectedTasksMap);

  const checkForNonMemberAssignees = () => {
    const projectMemberIds = currentProject?.team?.map(member => member.id) || [];
    const projectOwnerId = currentProject?.owner?.id;
    const allProjectUserIds = [...projectMemberIds, projectOwnerId].filter(Boolean);

    const nonMembers = [];

    selectedTasks.forEach(task => {
      if (task.assigneeUsers && task.assigneeUsers.length > 0) {
        task.assigneeUsers.forEach(assignee => {
          if (!allProjectUserIds.includes(assignee.id)) {
            if (!nonMembers.find(u => u.id === assignee.id)) {
              nonMembers.push({
                id: assignee.id,
                name: assignee.name || assignee.username,
                avatar: assignee.avatar,
              });
            }
          }
        });
      }
    });

    return nonMembers;
  };

  const handleAddTasksClick = () => {
    console.log('handleAddTasksClick called');
    console.log('selectedTaskIds:', selectedTaskIds);
    console.log('selectedTasks:', selectedTasks);

    const nonMemberAssignees = checkForNonMemberAssignees();
    console.log('nonMemberAssignees:', nonMemberAssignees);

    if (nonMemberAssignees.length > 0) {
      setAffectedUsers(nonMemberAssignees);
      setShowWarningModal(true);
    } else {
      handleConfirmAddTasks();
    }
  };

  const handleConfirmAddTasks = async () => {
    console.log('handleConfirmAddTasks called');
    try {
      await onAddTasks(selectedTaskIds, selectedTasks, selectedMilestoneIds);
      setShowWarningModal(false);
      handleCancel();
    } catch (error) {
      console.error('Error in handleConfirmAddTasks:', error);
    }
  };

  return (
    <Modal
      title={`Add Existing Tasks - Step ${currentStep} of 2`}
      open={visible}
      onCancel={handleCancel}
      width={900}
      centered
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Text style={{ color: '#64748b' }}>
            {selectedTaskIds.length} task(s) selected
          </Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              onClick={handleCancel}
              style={{
                fontSize: '14px',
                height: '40px',
                borderRadius: '5px',
              }}
            >
              Cancel
            </Button>
            {currentStep === 1 && (
              <Button
                type="primary"
                onClick={handleNext}
                disabled={selectedTaskIds.length === 0}
                style={{
                  fontSize: '14px',
                  height: '40px',
                  borderRadius: '5px',
                  backgroundColor: '#3b82f6',
                }}
              >
                Next
              </Button>
            )}
            {currentStep === 2 && (
              <>
                <Button
                  onClick={() => setCurrentStep(1)}
                  disabled={updateProjectLoading}
                  style={{
                    fontSize: '14px',
                    height: '40px',
                    borderRadius: '5px',
                  }}
                >
                  Back
                </Button>
                <Button
                  type="primary"
                  loading={updateProjectLoading}
                  disabled={updateProjectLoading}
                  onClick={handleAddTasksClick}
                  style={{
                    fontSize: '14px',
                    height: '40px',
                    borderRadius: '5px',
                    backgroundColor: '#3b82f6',
                  }}
                >
                  Add Tasks
                </Button>
              </>
            )}
          </div>
        </div>
      }
    >
      {currentStep === 1 && (
        <div>
          <Text style={{ display: 'block', marginBottom: 16, color: '#64748b' }}>
            Select the tasks you want to add to this project
          </Text>

          {/* Filters */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Text style={{ fontSize: '14px' }}>My Tasks</Text>
                <Switch
                  checked={showMyTasks}
                  onChange={(checked) => {
                    setShowMyTasks(checked);
                    setPagination({ ...pagination, current: 1 });
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                  <Button
                    type="text"
                    onClick={() => {
                      setSelectedTaskIds([]);
                      setSelectedTasksMap({});
                    }}
                    disabled={selectedTaskIds.length === 0}
                    style={{
                      fontSize: '13px',
                      color: '#64748b',
                    }}
                  >
                    Clear Selection
                  </Button>
                  <Input
                    placeholder="Search tasks"
                    value={searchText}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    style={{ width: 250, height: '40px', borderRadius: '5px' }}
                    prefix={
                      <Button
                        type="text"
                        icon={<CloseOutlined style={{ fontSize: '13px' }} />}
                        onClick={() => {
                          setSearchText('');
                          setPagination({ ...pagination, current: 1 });
                        }}
                      />
                    }
                  />
                </div>
              </div>
            </Col>
          </Row>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16, maxHeight: '400px', overflowY: 'auto' }}>
            {tasks.map((taskItem) => (
              <Card
                key={taskItem.id}
                hoverable
                onClick={() => handleTaskSelect(taskItem)}
                style={{
                  borderRadius: '7px',
                  border: selectedTaskIds.includes(taskItem.id)
                    ? '2px solid #3b82f6'
                    : '1px solid #e5edf4',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <Checkbox
                    checked={selectedTaskIds.includes(taskItem.id)}
                    onChange={() => handleTaskSelect(taskItem)}
                    onClick={(e) => e.stopPropagation()}
                  />

                  <div style={{ flex: 1 }}>
                    <Row gutter={[16, 8]} align="middle">
                      <Col flex="auto">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Tooltip title={taskItem.urgency ? `URGENCY: ${taskItem.urgency}` : 'NO URGENCY'}>
                              <Badge
                                dot
                                color={urgencyColors[taskItem.urgency] || 'white'}
                                className="custom-badge"
                              />
                            </Tooltip>
                            <Text strong style={{ fontSize: '15px' }}>
                              {taskItem.name}
                            </Text>
                          </div>
                          {taskItem.requestType && (
                            <Text style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '20px' }}>
                              {taskItem.requestType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                            </Text>
                          )}
                        </div>
                      </Col>

                      <Col>
                        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                          {taskItem.dueDate && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Text style={{ fontSize: '12px', color: '#64748b' }}>Due:</Text>
                              <Text style={{ fontSize: '12px' }}>
                                {moment(taskItem.dueDate).format('MMM D, YYYY')}
                              </Text>
                            </div>
                          )}

                          {taskItem.assigneeUsers && taskItem.assigneeUsers.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {taskItem.assigneeUsers.slice(0, 3).map((user, idx) => (
                                <Tooltip
                                  key={user.id || idx}
                                  title={startCase(user.name || user.username)}
                                >
                                  <AvatarS3
                                    imgKey={user.avatar && user.avatar.key}
                                    username={user.name || user.username}
                                    avatarProps={{ size: 24 }}
                                  />
                                </Tooltip>
                              ))}
                              {taskItem.assigneeUsers.length > 3 && (
                                <Text style={{ fontSize: '12px', color: '#64748b' }}>
                                  +{taskItem.assigneeUsers.length - 3}
                                </Text>
                              )}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {tasks.length === 0 && !tasksLoading && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              No tasks available
            </div>
          )}

          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePaginationChange}
            onShowSizeChange={handlePaginationChange}
            showSizeChanger
            style={{ marginTop: 16, textAlign: 'center' }}
          />
        </div>
      )}

      {currentStep === 2 && (
        <div>
          <Text style={{ fontSize: '16px', fontWeight: 'bold', display: 'block', marginBottom: 16 }}>
            Assign to Milestone
          </Text>

          <div style={{ marginBottom: 24 }}>
            <Text style={{ display: 'block', marginBottom: 12, color: '#64748b', fontSize: '14px' }}>
              Selected Tasks ({selectedTasks.length})
            </Text>
            <Card
              style={{
                backgroundColor: '#e6f4ff',
                borderRadius: '7px',
                border: '1px solid #91caff',
                marginBottom: 16,
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedTasks.map((task) => (
                  <Tag key={task.id} color="blue" style={{ fontSize: '12px', padding: '2px 8px', margin: 0 }}>
                    {task.name}
                  </Tag>
                ))}
              </div>
            </Card>
          </div>

          <div>
            <Text style={{ display: 'block', marginBottom: 12, color: '#64748b', fontSize: '14px' }}>
              Select Milestones (Optional)
            </Text>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '300px', overflowY: 'auto' }}>
              {milestonesLoading && (
                <Card
                  style={{
                    borderRadius: '7px',
                    border: '1px solid #e5edf4',
                    textAlign: 'center',
                    backgroundColor: '#fafafa',
                  }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
                    Loading milestones...
                  </Text>
                </Card>
              )}

              {!milestonesLoading && (!milestones || milestones.length === 0) && (
                <Card
                  style={{
                    borderRadius: '7px',
                    border: '1px solid #e5edf4',
                    textAlign: 'center',
                    backgroundColor: '#fafafa',
                  }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
                    No milestones available. Add a milestone first or skip this step.
                  </Text>
                </Card>
              )}


              {!milestonesLoading && milestones?.map((milestone) => (
                <Card
                  key={milestone.id}
                  hoverable
                  onClick={() => handleMilestoneSelect(milestone.id)}
                  style={{
                    borderRadius: '7px',
                    border: selectedMilestoneIds.includes(milestone.id)
                      ? '2px solid #3b82f6'
                      : '1px solid #e5edf4',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Checkbox
                      checked={selectedMilestoneIds.includes(milestone.id)}
                      onChange={() => handleMilestoneSelect(milestone.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Text strong style={{ fontSize: '14px' }}>
                      {milestone.name}
                    </Text>
                  </div>
                </Card>
              ))}
            </div>

            <Text style={{ display: 'block', marginTop: 12, color: '#8c8c8c', fontSize: '12px' }}>
              You can add tasks without selecting a milestone
            </Text>
          </div>
        </div>
      )}

      {/* Warning Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '22px' }} />
            <span>Assignee Conversion Warning</span>
          </div>
        }
        open={showWarningModal}
        onCancel={() => setShowWarningModal(false)}
        width={600}
        centered
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              onClick={() => setShowWarningModal(false)}
              disabled={updateProjectLoading}
              style={{
                fontSize: '14px',
                height: '40px',
                borderRadius: '5px',
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={updateProjectLoading}
              disabled={updateProjectLoading}
              onClick={handleConfirmAddTasks}
              style={{
                fontSize: '14px',
                height: '40px',
                borderRadius: '5px',
                backgroundColor: '#faad14',
                borderColor: '#faad14',
              }}
            >
              Confirm & Add Tasks
            </Button>
          </div>
        }
      >
        <div>
          <Text style={{ display: 'block', marginBottom: 16, fontSize: '14px' }}>
            The following users are assigned to the selected tasks but are not members or owners of this project:
          </Text>

          <Card
            style={{
              backgroundColor: '#fffbe6',
              borderRadius: '7px',
              border: '1px solid #ffe58f',
              marginBottom: 16,
            }}
            bodyStyle={{ padding: '12px' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {affectedUsers.map((user) => (
                <div key={user.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AvatarS3
                    imgKey={user.avatar && user.avatar.key}
                    username={user.name}
                    avatarProps={{ size: 24 }}
                  />
                  <Text style={{ fontSize: '14px' }}>{startCase(user.name)}</Text>
                </div>
              ))}
            </div>
          </Card>

          <Text style={{ display: 'block', fontSize: '14px', color: '#8c8c8c' }}>
            When you proceed, these users will be converted to <strong>collaborators</strong> on the tasks instead of assignees. They will still be able to view and work on the tasks.
          </Text>
        </div>
      </Modal>
    </Modal>
  );
};

export default AddExistingTaskModal;
